import {PricingOptions, Tabs, Box} from '@primer/react-brand'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {InfoIcon} from '@primer/octicons-react'

import type {PricingPlan, PricingOption} from '../../../types'
import {renderWithNoMarks} from '../../../utils'

export function PricingSectionPlans({options}: {options: PricingOption[]}) {
  const hasMultipleOptions = options.length > 1

  // When multiple pricing options exist (e.g. "For individuals" / "For businesses"),
  // render them as tabs so users can switch between plan groups
  if (hasMultipleOptions) {
    return (
      <Tabs aria-label="Pricing plans" variant="accent">
        {options.map(option => (
          <Tabs.Item key={option.sys.id}>{option.fields.label}</Tabs.Item>
        ))}
        {options.map(option => (
          <Tabs.Panel key={option.sys.id}>{renderPlansForOption(option)}</Tabs.Panel>
        ))}
      </Tabs>
    )
  }

  // Single pricing option: render plans directly without tabs
  return renderPlansForOption(options[0]!)
}

function renderPlansForOption(option: PricingOption) {
  const {plans, mostPopular} = option.fields

  return (
    <Box paddingBlockStart={{narrow: 'normal', regular: 'spacious'}}>
      <PricingOptions variant="default">
        {plans.map((plan, index) => renderPlanItem(plan, mostPopular === index))}
      </PricingOptions>
    </Box>
  )
}

function renderPlanItem(plan: PricingPlan, isMostPopular: boolean) {
  const {heading, description, price, originalPrice, priceTrailing, primaryCta, secondaryCta, features, trailingText} =
    plan.fields

  return (
    <PricingOptions.Item key={plan.sys.id}>
      <PricingOptions.Heading>{renderWithNoMarks(heading)}</PricingOptions.Heading>
      {isMostPopular && <PricingOptions.Label>Most popular</PricingOptions.Label>}
      <PricingOptions.Description>{renderWithNoMarks(description)}</PricingOptions.Description>
      <PricingOptions.Price originalPrice={originalPrice} trailingText={priceTrailing}>
        {price}
      </PricingOptions.Price>
      {primaryCta && (
        <PricingOptions.PrimaryAction as="a" href={primaryCta.fields.href} variant="accent">
          {primaryCta.fields.text}
        </PricingOptions.PrimaryAction>
      )}
      {secondaryCta && (
        <PricingOptions.SecondaryAction as="a" href={secondaryCta.fields.href}>
          {secondaryCta.fields.text}
        </PricingOptions.SecondaryAction>
      )}
      {features && features.length > 0 && (
        <PricingOptions.FeatureList>
          {features.map(feature => (
            <PricingOptions.FeatureListItem
              key={feature}
              leadingVisual={InfoIcon}
              leadingVisualFill="var(--brand-color-text-muted)"
            >
              {feature}
            </PricingOptions.FeatureListItem>
          ))}
        </PricingOptions.FeatureList>
      )}
      {trailingText && <PricingOptions.Footnote>{documentToPlainTextString(trailingText)}</PricingOptions.Footnote>}
    </PricingOptions.Item>
  )
}
